<template>
  <div class="row mt-10 align-items-center">
    <div id="pag-navigation" class="col-8">
      <nav aria-label="Table navigation">
        <ul class="pagination m-0" :class="{ 'pagination-pills': pills }">
          <!-- First button -->

          <li class="page-item">
            <a
              v-if="currentPage > 1"
              class="page-link"
              role="button"
              aria-label="First"
              data-test="first-button"
              @click="emitPageEvent(1)"
            >
              <span aria-hidden="true">&laquo;</span>
              <span class="sr-only">{{ translate("SHARED.PAGINATION.FIRST") }}</span>
            </a>

            <a v-else class="page-link" aria-label="First" data-test="first-button">
              <span aria-hidden="true">&laquo;</span>
              <span class="sr-only">{{ translate("SHARED.PAGINATION.FIRST") }}</span>
            </a>
          </li>

          <!-- Previous button -->

          <li class="page-item">
            <a
              v-if="currentPage > 1"
              class="page-link"
              role="button"
              aria-label="Previous"
              @click="emitPageEvent(currentPage - 1)"
              data-test="previous-button"
            >
              <span aria-hidden="true">&lsaquo;</span>
              <span class="sr-only">{{ translate("SHARED.NAVIGATION.PREVIOUS") }}</span>
            </a>

            <a v-else class="page-link" aria-label="Previous" data-test="previous-button">
              <span aria-hidden="true">&lsaquo;</span>
              <span class="sr-only">{{ translate("SHARED.NAVIGATION.PREVIOUS") }}</span>
            </a>
          </li>

          <!-- Etc left button -->

          <li v-if="currentPage > 2" class="page-item">
            <a class="page-link">...</a>
          </li>

          <!-- Pages buttons iteration -->

          <li v-for="page in pageLinks" :key="page" class="page-item" :class="{ active: page === currentPage }">
            <a class="page-link" role="button" @click="emitPageEvent(page)" data-test="page-button">{{ page }}</a>
          </li>

          <!-- Etc right button -->

          <li v-if="currentPage < totalPages - 1" class="page-item">
            <a class="page-link">...</a>
          </li>

          <!-- Next button -->

          <li class="page-item">
            <a
              v-if="currentPage < totalPages"
              class="page-link"
              role="button"
              aria-label="Next"
              @click="emitPageEvent(currentPage + 1)"
              data-test="next-button"
            >
              <span aria-hidden="true">&rsaquo;</span>
              <span class="sr-only">{{ translate("SHARED.NAVIGATION.NEXT") }}</span>
            </a>

            <a v-else class="page-link" aria-label="Next" data-test="next-button">
              <span aria-hidden="true">&rsaquo;</span>
              <span class="sr-only">{{ translate("SHARED.NAVIGATION.NEXT") }}</span>
            </a>
          </li>

          <!-- Last button -->

          <li class="page-item">
            <a
              v-if="currentPage < totalPages"
              class="page-link"
              role="button"
              aria-label="Last"
              @click="emitPageEvent(totalPages)"
              data-test="last-button"
            >
              <span aria-hidden="true">&raquo;</span>
              <span class="sr-only">{{ translate("SHARED.PAGINATION.LAST") }}</span>
            </a>

            <a v-else class="page-link" aria-label="Last" data-test="last-button">
              <span aria-hidden="true">&raquo;</span>
              <span class="sr-only">{{ translate("SHARED.PAGINATION.LAST") }}</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>

    <div class="col" data-test="per-page-select">
      <select class="custom-select custom-select-sm" @change="emitPerPageEvent($event.target.value)" v-model="perPage">
        <option v-for="perPageOption of perPageOptions" :value="perPageOption" :key="perPageOption">
          {{ perPageOption }}
        </option>
      </select>
    </div>

    <div data-test="nb-of-items-dispayed" class="col" :class="{ 'mt-5': isViewportMobile() }">
      {{
        `${ressourceName} ${translate("SHARED.PAGINATION.LIST", {
          firstItemDisplayed: currentPage * perPage - (perPage - 1),
          lastItemDisplayed: nbOfItemsDisplayed,
          totalDisplayed: totalItems,
        })}`
      }}
    </div>
  </div>
</template>

<script>
import useTranslate from "@/core/composables/translate";

export default {
  name: "TablePagination",

  inject: ["isViewportMobile"],

  emits: ["newPage", "newPerPage"],

  props: {
    totalItems: {
      type: Number,
      required: true,
      default: 0,
    },

    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },

    currentPerPage: {
      type: Number,
      required: true,
      default: 5,
    },

    totalPages: {
      type: Number,
      required: true,
      default: 0,
    },

    perPageOptions: {
      type: Array,
      required: true,
      default: () => [5, 10, 15, 30],
    },

    ressourceName: {
      type: String,
      required: true,
    },

    nbOfItemsDisplayed: {
      type: Number,
      required: true,
      default: 0,
    },

    pills: {
      type: Boolean,
      required: false,
    },
  },

  setup() {
    const { translate } = useTranslate();
    return {
      translate,
    };
  },

  data() {
    return {
      perPage: this.currentPerPage,
    };
  },

  methods: {
    /**
     * @param {number} newPage
     * @returns {void}
     */
    emitPageEvent(newPage) {
      this.$emit("newPage", Number(newPage));
    },

    /**
     * @returns {void}
     */
    emitPerPageEvent(newPerPage) {
      this.$emit("newPerPage", Number(newPerPage));
    },
  },
  computed: {
    pageLinks() {
      if (this.currentPage === this.totalPages) {
        return [this.currentPage];
      }

      return [this.currentPage, this.currentPage + 1];
    },
  },
};
</script>
